import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import PublicFooter from "../components/PublicFooter";
import { api, formatApiErrorDetail, STATUS_LABEL } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import {
  Search,
  Loader2,
  FileSearch,
  Clock,
  MapPin,
  Calendar,
  RefreshCw,
  Shield,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  FileText,
  ArrowRight,
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const STATUS_META = {
  baru: { color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", dot: "bg-blue-500", icon: FileText },
  diverifikasi: { color: "text-yellow-600", bg: "bg-yellow-50", border: "border-yellow-200", dot: "bg-yellow-500", icon: CheckCircle2 },
  dalam_penyelidikan: { color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200", dot: "bg-orange-500", icon: Search },
  selesai: { color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500", icon: CheckCircle2 },
  ditolak: { color: "text-red-600", bg: "bg-red-50", border: "border-red-200", dot: "bg-red-500", icon: AlertTriangle },
};

export default function TrackPage() {
  const [params] = useSearchParams();
  const prefill = params.get("nomor") || "";
  const [nomor, setNomor] = useState(prefill);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const search = async (e) => {
    if (e) e.preventDefault();
    if (!nomor.trim()) return;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const res = await api.get(`/reports/track/${encodeURIComponent(nomor.trim())}`);
      setData(res.data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Laporan tidak ditemukan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (prefill) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const statusMeta = data ? (STATUS_META[data.status] || STATUS_META.baru) : null;

  return (
    <div data-testid="track-page" className="min-h-screen flex flex-col" style={{ background: "#f1f5f9" }}>
      <PublicNavbar />

      {/* ── Hero Banner ────────────────────────────────────── */}
      <section
        className="relative overflow-hidden pt-32 pb-12 lg:pt-40 lg:pb-16"
        style={{ background: "linear-gradient(135deg, #0b2b5e 0%, #0f3d7a 50%, #061830 100%)" }}
      >
        {/* Background grid */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "40px 40px"
        }} aria-hidden />
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "28px 28px", opacity: 0.08
        }} aria-hidden />

        {/* Glow orbs */}
        <div className="absolute -top-20 right-0 h-80 w-80 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(250,204,21,0.15) 0%, transparent 70%)" }} aria-hidden />
        <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(6,182,212,0.1) 0%, transparent 70%)" }} aria-hidden />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="hidden sm:flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-slate-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Beranda</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-yellow-400">Lacak Laporan</span>
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
            style={{ background: "rgba(250,204,21,0.12)", border: "1px solid rgba(250,204,21,0.3)" }}>
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" style={{ animation: "livePulse 1.8s ease-out infinite" }} />
            <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-yellow-400">Sistem Aktif 24/7</span>
          </div>

          {/* Title */}
          <h1 className="font-display font-black text-white tracking-tighter"
            style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)", lineHeight: "1.05" }}>
            Lacak Status<br />
            <span style={{
              background: "linear-gradient(135deg, #facc15 0%, #f59e0b 40%, #ffffff 70%, #facc15 100%)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              animation: "shimmer 4s linear infinite",
            }}>Laporan Anda</span>
          </h1>
          <p className="mt-4 text-slate-300 max-w-xl leading-relaxed">
            Masukkan nomor laporan yang Anda terima saat mengirim laporan untuk melihat status terkini dan riwayat penanganan.
          </p>

          {/* Search form — in the hero */}
          <form onSubmit={search} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={nomor}
                onChange={(e) => setNomor(e.target.value.toUpperCase())}
                data-testid="track-input"
                placeholder="LAP-20260215-AB3X9"
                className="h-13 pl-10 font-mono-tight font-semibold text-base bg-white/10 text-white placeholder:text-slate-400 border-white/20 focus:border-yellow-400 focus:bg-white/15"
                style={{ height: "52px", background: "rgba(255,255,255,0.08)", backdropFilter: "blur(8px)" }}
              />
            </div>
            <button
              type="submit"
              data-testid="track-submit-btn"
              disabled={loading}
              className="btn-shimmer inline-flex items-center justify-center gap-2 rounded-xl px-7 font-bold text-sm text-[#0b2b5e] shadow-lg hover:scale-105 transition-all disabled:opacity-60 disabled:scale-100 flex-shrink-0"
              style={{ height: "52px", background: "linear-gradient(135deg, #facc15 0%, #f59e0b 100%)", minWidth: "150px" }}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Cari Laporan
            </button>
          </form>

          {/* Hint */}
          <p className="mt-3 text-[11px] text-slate-400 flex items-center gap-1.5">
            <Shield className="h-3 w-3" />
            Nomor laporan dikirimkan setelah laporan berhasil disubmit.
          </p>
        </div>
      </section>

      {/* ── Results Section ────────────────────────────────── */}
      <section className="max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 flex-1">

        {/* Empty state */}
        {!loading && !error && !data && (
          <div className="text-center py-16">
            <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl mb-6"
              style={{ background: "rgba(11,43,94,0.06)", border: "1px solid rgba(11,43,94,0.1)" }}>
              <FileSearch className="h-9 w-9 text-[#0B2B5E] opacity-40" />
            </div>
            <p className="text-slate-500 text-sm max-w-sm mx-auto">
              Masukkan nomor laporan di atas untuk melihat status penanganan laporan Anda.
            </p>
            <Link to="/lapor"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0B2B5E] hover:text-blue-700 transition-colors">
              Belum punya laporan? Buat sekarang <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl mb-4"
              style={{ background: "rgba(11,43,94,0.06)" }}>
              <Loader2 className="h-7 w-7 text-[#0B2B5E] animate-spin" />
            </div>
            <p className="text-slate-500 text-sm">Mencari laporan...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div data-testid="track-error"
            className="rounded-2xl overflow-hidden shadow-sm"
            style={{ border: "1px solid #fecaca" }}>
            <div className="h-1 w-full bg-red-400" />
            <div className="p-6 bg-white flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <FileSearch className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <div className="font-display font-bold text-red-700">Laporan tidak ditemukan</div>
                <div className="text-sm mt-1 text-red-600 leading-relaxed">{error}</div>
                <button onClick={() => { setError(""); setNomor(""); }}
                  className="mt-3 text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors">
                  <RefreshCw className="h-3 w-3" /> Coba nomor lain
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Result */}
        {data && (
          <div data-testid="track-result" className="space-y-5">

            {/* Main status card */}
            <div className="rounded-2xl overflow-hidden shadow-sm bg-white" style={{ border: "1px solid #e2e8f0" }}>
              <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg, #0b2b5e, #3b82f6, #06b6d4)" }} />
              <div className="p-6 sm:p-8">

                {/* Header row */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-6 border-b border-slate-100">
                  <div>
                    <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-slate-400 mb-1">Nomor Laporan</div>
                    <div className="font-mono-tight font-black text-2xl text-[#0B2B5E]">{data.nomor_laporan}</div>
                  </div>
                  <div className="flex flex-col items-start sm:items-end gap-2">
                    <StatusBadge status={data.status} testId="track-status-badge" />
                    {statusMeta && (
                      <div className={`text-xs font-medium ${statusMeta.color}`}>
                        {STATUS_LABEL[data.status] || data.status}
                      </div>
                    )}
                  </div>
                </div>

                {/* Info grid */}
                <div className="grid sm:grid-cols-2 gap-5 mt-6">
                  {[
                    { icon: FileText, label: "Jenis Narkoba", val: data.jenis_narkoba, color: "text-purple-600", bg: "bg-purple-50" },
                    { icon: MapPin, label: "Wilayah", val: data.kabupaten_kota, color: "text-blue-600", bg: "bg-blue-50" },
                    { icon: Calendar, label: "Dilaporkan", val: formatDate(data.created_at), color: "text-slate-500", bg: "bg-slate-100" },
                    { icon: RefreshCw, label: "Pembaruan Terakhir", val: formatDate(data.updated_at), color: "text-emerald-600", bg: "bg-emerald-50" },
                  ].map(({ icon: Icon, label, val, color, bg }) => (
                    <div key={label} className="flex items-start gap-3">
                      <div className={`h-8 w-8 rounded-lg ${bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Icon className={`h-4 w-4 ${color}`} />
                      </div>
                      <div>
                        <div className="text-[10px] tracking-wider uppercase font-bold text-slate-400">{label}</div>
                        <div className="mt-0.5 font-semibold text-[#0F172A] text-sm">{val}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Admin note */}
                {data.catatan_admin && (
                  <div className="mt-6 rounded-xl p-4 flex items-start gap-3"
                    style={{ background: "rgba(11,43,94,0.04)", border: "1px solid rgba(11,43,94,0.1)" }}>
                    <div className="h-7 w-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Shield className="h-3.5 w-3.5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold text-[#0B2B5E] mb-1">Catatan Petugas</div>
                      <div className="text-sm text-slate-700 leading-relaxed">{data.catatan_admin}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* History card */}
            <div className="rounded-2xl overflow-hidden shadow-sm bg-white" style={{ border: "1px solid #e2e8f0" }}>
              <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg, #facc15, #f59e0b)" }} />
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-8 w-8 rounded-lg bg-yellow-50 flex items-center justify-center">
                    <Clock className="h-4 w-4 text-yellow-600" />
                  </div>
                  <div className="font-display font-bold text-[#0F172A] text-lg">Riwayat Penanganan</div>
                </div>

                {(data.history || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Belum ada riwayat status.</p>
                ) : (
                  <ol className="relative ml-3">
                    {(data.history || []).map((h, idx) => {
                      const isLast = idx === (data.history.length - 1);
                      return (
                        <li key={h.id || idx} className="relative pl-8 pb-7 last:pb-0" data-testid={`history-item-${idx}`}>
                          {/* Line */}
                          {!isLast && (
                            <div className="absolute left-[7px] top-6 bottom-0 w-0.5 bg-slate-200" />
                          )}
                          {/* Dot */}
                          <div className={`absolute left-0 top-1 h-4 w-4 rounded-full border-2 border-white shadow-sm ${
                            isLast ? "bg-[#0B2B5E]" : "bg-slate-300"
                          }`} style={isLast ? { boxShadow: "0 0 0 3px rgba(11,43,94,0.15)" } : {}} />

                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <StatusBadge status={h.to_status} />
                              <span className="text-xs text-slate-400">{formatDate(h.changed_at)}</span>
                            </div>
                            {h.catatan && (
                              <div className="text-sm text-slate-700 leading-relaxed">{h.catatan}</div>
                            )}
                            {h.from_status && (
                              <div className="mt-1 text-[11px] text-slate-400">
                                Sebelumnya: {STATUS_LABEL[h.from_status] || h.from_status}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>

            {/* Back action */}
            <div className="text-center pt-2">
              <button onClick={() => { setData(null); setNomor(""); }}
                className="text-sm text-slate-500 hover:text-[#0B2B5E] transition-colors flex items-center gap-1.5 mx-auto">
                <RefreshCw className="h-3.5 w-3.5" /> Lacak nomor laporan lain
              </button>
            </div>
          </div>
        )}
      </section>

      <PublicFooter />
    </div>
  );
}
